import { useState, type FormEvent } from "react";
import Seo from "@/components/feature/Seo";
import PageHeader from "@/components/feature/PageHeader";
import { siteMeta } from "@/mocks/manga";

const FORM_URL = "https://readdy.ai/api/form/da2jmtbd3r93mn1k3a80";

const contactInfo = [
  { icon: "ri-mail-line", title: "邮箱", value: "support@sweetmanga.example.com" },
  { icon: "ri-time-line", title: "更新状态", value: "连载与番外内容持续跟进整理" },
  { icon: "ri-chat-3-line", title: "反馈建议", value: "欢迎提出阅读体验方面的建议" },
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honey = String(fd.get("phone_alt") || "").trim();
    if (honey) {
      setStatus("success");
      form.reset();
      return;
    }
    fd.delete("phone_alt");

    const name = String(fd.get("name") || "").trim();
    const email = String(fd.get("email") || "").trim();
    const subject = String(fd.get("subject") || "").trim();
    const message = String(fd.get("message") || "").trim();

    if (!name || !email || !message) {
      setFormError("请填写姓名、邮箱与留言内容。");
      setStatus("error");
      return;
    }
    if (message.length > 500) {
      setFormError("留言内容请不要超过500字。");
      setStatus("error");
      return;
    }

    setStatus("loading");
    setFormError("");
    try {
      const params = new URLSearchParams();
      params.set("name", name);
      params.set("email", email);
      if (subject) params.set("subject", subject);
      params.set("message", message);

      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const msg =
        parsed?.meta?.message || parsed?.meta?.detail || (responseText ? responseText : "");

      if (res.ok && parsed?.code === "OK" && !/spam|form data is spam/i.test(msg)) {
        setStatus("success");
        form.reset();
      } else {
        setFormError(msg || "提交失败，请稍后再试。");
        setStatus("error");
      }
    } catch {
      setFormError("网络异常，请稍后再试。");
      setStatus("error");
    }
  }

  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "联系我们",
    description: `联系${siteMeta.name}，反馈关于甜蜜惩罚漫画阅读的建议与问题。`,
  };

  return (
    <>
      <Seo
        title={`联系我们 - ${siteMeta.name}`}
        description={`联系${siteMeta.name}：反馈关于甜蜜惩罚漫画单行本、连载、全彩与番外收录的意见与问题。`}
        keywords={siteMeta.keywords}
        canonicalPath="/contact"
        schema={contactSchema}
      />

      <PageHeader
        title="联系我们"
        subtitle="有任何阅读问题、目录建议或内容反馈，欢迎留言告诉我们。"
        crumbs={[{ label: "首页", to: "/" }, { label: "联系我们" }]}
      />

      <div className="bg-background-50 py-12 md:py-16">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 md:px-6 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">联系方式</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              你的每一条反馈，都会帮助我们更好地整理甜蜜惩罚漫画的完整目录，让更多读者读得更顺手。
            </p>
            <ul className="mt-7 space-y-4">
              {contactInfo.map((c) => (
                <li key={c.title} className="flex items-start gap-4 rounded-2xl bg-background-100 p-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                    <i className={`${c.icon} text-xl`}></i>
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-foreground-900">{c.title}</span>
                    <span className="mt-0.5 block text-sm text-foreground-600">{c.value}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <form data-readdy-form onSubmit={handleSubmit} className="rounded-3xl bg-background-100 p-6 md:p-8">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">发送留言</h2>

              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    你的称呼
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    autoComplete="name"
                    placeholder="怎么称呼你"
                    className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    邮箱地址
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="name@example.com"
                    className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="subject" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  主题
                </label>
                <input
                  id="subject"
                  name="subject"
                  type="text"
                  placeholder="例如：目录建议"
                  className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>

              <div className="mt-4">
                <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  留言内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="请写下你想反馈的内容（500字以内）"
                  className="w-full resize-none rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                ></textarea>
              </div>

              <div className="trap-field" aria-hidden="true">
                <label htmlFor="phone_alt">手机</label>
                <input
                  id="phone_alt"
                  name="phone_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              {status === "success" && (
                <p className="mt-4 rounded-xl bg-accent-100 px-4 py-3 text-sm text-accent-800">
                  感谢你的留言，我们会尽快阅读并回复。
                </p>
              )}
              {status === "error" && formError && (
                <p className="mt-4 rounded-xl bg-primary-100 px-4 py-3 text-sm text-primary-800">{formError}</p>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 px-7 py-3 text-sm font-semibold whitespace-nowrap text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === "loading" ? "发送中..." : "提交留言"}
                <i className="ri-send-plane-line"></i>
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}